import AWSAppSyncClient, { AUTH_TYPE } from 'aws-appsync';
import gql from 'graphql-tag';
import {
  PROPERTY_SERVICE_V5_AWS_CONFIG,
  INTEGRATION_SERVICE_V5_AWS_CONFIG,
} from '../env/Configurations';
import {
  getAnalyticsReservationList,
  getTotalReservationStats,
  getAnalyticsModulePermissions,
  getAnalyticsSpaAppointmentList,
  getAnalyticsTotalSpaAppointments,
  getAnalyticsMostBookedSpaTreatments,
  getAnalyticsTotalHKRequest,
  getHKAnalyticsServiceDetails,
  getTotalIRDOrderDetails,
  getAnalyticsCheckInStats,
  getAnalyticsUsageStats,
  getTopIrdItems,
  listHKAnalyticsServiceActivity,
  getTopIrdItemsFilter,
  listItemActivity,
  listSPAAnalyticsServiceActivity,
} from '../graphql/analytics/queries';

import { getCurrentJwtToken, getEncodedValue } from './authUtils';

export const propertyServiceV5Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V5_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V5_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

export const integrationServiceV5Client = new AWSAppSyncClient({
  url: INTEGRATION_SERVICE_V5_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: INTEGRATION_SERVICE_V5_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

export const getAnalyticsReservationListData = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getAnalyticsReservationList),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      restaurantId: payload.restaurantId,
      startDate: payload.startDate,
      endDate: payload.endDate,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getTotalReservationsStats = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getTotalReservationStats),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      restaurantId: payload.restaurantId,
      startDate: payload.startDate,
      endDate: payload.endDate,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getAnalyticsSpaAppointmentsList = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getAnalyticsSpaAppointmentList),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      spaId: payload.spaId,
      startDate: payload.startDate,
      endDate: payload.endDate,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getAnalyticsTotalSpaAppointment = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getAnalyticsTotalSpaAppointments),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      spaId: payload.spaId,
      startDate: payload.startDate,
      endDate: payload.endDate,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getAnalyticsModulePermissionList = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getAnalyticsModulePermissions),
    variables: getEncodedValue({
      groupId: payload?.groupId,
      hotelId: payload?.hotelId,
      brandId: payload?.brandId,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getAnalyticsMostBookedSpaTreatment = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getAnalyticsMostBookedSpaTreatments),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      spaId: payload.spaId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      lang: payload.lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getAnalyticsTotalHKRequests = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getAnalyticsTotalHKRequest),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
    }),
    fetchPolicy: 'network-only',
  });
};

export const listHKAnalyticsServiceActivityData = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(listHKAnalyticsServiceActivity),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      lang: payload.lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getHKAnalyticsServiceDetailsData = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getHKAnalyticsServiceDetails),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      categoryType: payload.categoryType,
      lang: payload.lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getHKAnalyticsServiceDetailsFilterData = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getHKAnalyticsServiceDetails),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      categoryType: payload.categoryType,
      lang: payload.lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getTotalIRDOrdersDetail = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getTotalIRDOrderDetails),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      lang: payload.lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getDigitalCheckInStats = async (payload) => {
  return await integrationServiceV5Client.query({
    query: gql(getAnalyticsCheckInStats),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      channel: payload.channel,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getAnalyticsUsageState = async (payload) => {
  return await integrationServiceV5Client.query({
    query: gql(getAnalyticsUsageStats),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getTopIrdOrderItems = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getTopIrdItems),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      categoryId: payload.categoryId,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getTopIrdOrderItemsFilter = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(getTopIrdItemsFilter),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      categoryId: payload.categoryId,
    }),
    fetchPolicy: 'network-only',
  });
};

export const listItemActivityData = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(listItemActivity),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      isUpsell: false,
    }),
    fetchPolicy: 'network-only',
  });
};

export const listUpsellItemActivityData = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(listItemActivity),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      isUpsell: true,
    }),
    fetchPolicy: 'network-only',
  });
};

export const listSPAAnalyticsServiceActivityData = async (payload) => {
  return await propertyServiceV5Client.query({
    query: gql(listSPAAnalyticsServiceActivity),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      spaId: payload.spaId,
      lang: payload.lang,
    }),
    fetchPolicy: 'network-only',
  });
};
